import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Avatar,
    Stack,
    Divider,
    useTheme,
    Paper,
    Fade,
    IconButton,
    Tooltip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarsIcon from '@mui/icons-material/Stars';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import SchoolIcon from '@mui/icons-material/School';
import InfoIcon from '@mui/icons-material/Info';
import {createGamificationService, GamificationService} from '../../services/GamificationService';

export const StudentGamification = ({accessToken, userId}) => {
    const theme = useTheme();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [leaderboardData, setLeaderboardData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const gamificationService = createGamificationService(accessToken);
                const data = await gamificationService.getGamificationSummary(userId);
                console.log('📊 Gamification data received:', data); // Debug
                setSummary(data);
                
                // Fetch leaderboard data
                const leaderboard = await gamificationService.getLeaderboard(20, userId);
                setLeaderboardData(leaderboard);
            } catch (error) {
                console.error('❌ Error fetching gamification data:', error);
                // Set empty data so UI doesn't hang
                setSummary({
                    stats: null,
                    recent_achievements: [],
                    recent_transactions: [],
                    leaderboard_position: 0,
                    next_level_xp: 0,
                    xp_to_next_level: 0
                });
            } finally {
                setLoading(false);
            }
        };
        if (accessToken && userId) fetchData();
    }, [accessToken, userId]);

    if (loading || !summary) return (
        <Container maxWidth="lg">
            <Box sx={{mt: 8, textAlign: 'center'}}>
                <Typography variant="h6" color="text.secondary">Loading your stats...</Typography>
            </Box>
        </Container>
    );

    const {
        stats,
        recent_achievements,
        recent_transactions,
        leaderboard_position,
        next_level_xp,
        xp_to_next_level
    } = summary;

    // Use the same level progress calculation as the Dashboard
    const levelProgress = GamificationService.getLevelProgress(
        Number(stats.total_xp),
        Number(stats.current_level)
    );

    return (
        <Container maxWidth="lg" sx={{mt: 6, mb: 8}}>
            {/* Modern Header */}
            <Fade in timeout={800}>
                <Box sx={{mb: 6}}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            mb: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <TrendingUpIcon sx={{mr: 2, color: theme.palette.primary.main, fontSize: '2.5rem'}}/>
                        Your Learning Progress
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{fontWeight: 400}}>
                        Track your journey and achievements
                    </Typography>
                </Box>
            </Fade>

            {/* Main Stats Row */}
            <Grid container spacing={3} sx={{mb: 4}}>
                {/* Level Card */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Fade in timeout={1000}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                color: 'white',
                                position: 'relative',
                                overflow: 'hidden',
                                height: '100%',
                            }}
                        >
                            <Box sx={{position: 'relative', zIndex: 1}}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{mb: 3}}>
                                    <Avatar
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            background: `linear-gradient(135deg, ${theme.palette.gamification.gold}, ${theme.palette.gamification.warning})`,
                                            fontSize: '1.8rem',
                                            boxShadow: '0 4px 20px rgba(255,215,0,0.3)'
                                        }}
                                    >
                                        <SchoolIcon/>
                                    </Avatar>
                                    <Box sx={{flexGrow: 1}}>
                                        <Typography variant="h4" sx={{
                                            fontWeight: 800,
                                            mb: 0.5,
                                            lineHeight: 1,
                                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }}>
                                            Level {stats.current_level}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            opacity: 0.8,
                                            mt: 0.5,
                                            textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>
                                            {stats.total_xp} XP total
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center"
                                           sx={{mb: 1}}>
                                        <Typography variant="body2" sx={{
                                            opacity: 0.9,
                                            fontWeight: 600,
                                            textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>
                                            Progress in this level
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            opacity: 0.9,
                                            fontWeight: 600,
                                            textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>
                                            {Math.round(levelProgress.percentage || 0)}%
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={levelProgress.percentage || 0}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            background: 'rgba(255,255,255,0.2)',
                                            '& .MuiLinearProgress-bar': {
                                                background: `linear-gradient(90deg, ${theme.palette.gamification.gold}, ${theme.palette.gamification.warning})`,
                                                borderRadius: 5,
                                                boxShadow: '0 2px 8px rgba(255,215,0,0.4)'
                                            }
                                        }}
                                    />
                                    <Typography variant="caption"
                                                sx={{opacity: 0.8, mt: 1, display: 'block', textAlign: 'right'}}>
                                        {levelProgress.progressXp} / {levelProgress.neededXp} XP
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Coins Card */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Fade in timeout={1200}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: `2px solid ${theme.palette.gamification.coins}40`,
                                background: `${theme.palette.gamification.coins}10`,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{width: '100%'}}>
                                <Avatar sx={{
                                    background: theme.palette.gamification.coins,
                                    width: 50,
                                    height: 50
                                }}>
                                    <MonetizationOnIcon/>
                                </Avatar>
                                <Box>
                                    <Typography variant="h4"
                                                sx={{fontWeight: 700, color: theme.palette.gamification.coins}}>
                                        {stats.total_coins}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Coins Earned
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Streak Card */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Fade in timeout={1400}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: `2px solid ${theme.palette.gamification.streak}40`,
                                background: `${theme.palette.gamification.streak}10`,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{width: '100%'}}>
                                <Avatar sx={{
                                    background: theme.palette.gamification.streak,
                                    width: 50,
                                    height: 50
                                }}>
                                    <LocalFireDepartmentIcon/>
                                </Avatar>
                                <Box>
                                    <Typography variant="h4"
                                                sx={{fontWeight: 700, color: theme.palette.gamification.streak}}>
                                        {stats.streak_days}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Day Streak
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Rank Card */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Fade in timeout={1600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: `2px solid ${theme.palette.gamification.secondary}40`,
                                background: `${theme.palette.gamification.secondary}10`,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2} sx={{width: '100%'}}>
                                <Avatar sx={{
                                    background: theme.palette.gamification.secondary,
                                    width: 50,
                                    height: 50
                                }}>
                                    <LeaderboardIcon/>
                                </Avatar>
                                <Box>
                                    <Typography variant="h4"
                                                sx={{fontWeight: 700, color: theme.palette.gamification.secondary}}>
                                        #{stats.rank_position || '-'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Global Rank
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>
            {/* Bottom Section: Achievements, Leaderboard, Recent Activity */}
            <Grid container spacing={3}>
                {/* Achievements */}
                <Grid size={{xs: 12, md: 4}}>
                    <Fade in timeout={1800}>
                        <Paper elevation={0} sx={{p: 3, height: '100%'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 3}}>
                                <Typography variant="h6" sx={{fontWeight: 700, color: theme.palette.primary.main}}>
                                    <StarsIcon sx={{mr: 1, color: theme.palette.gamification.gold}}/>
                                    Achievements
                                </Typography>
                                <Tooltip title="Your unlocked achievements">
                                    <IconButton size="small">
                                        <InfoIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            <Stack spacing={2}>
                                {recent_achievements && recent_achievements.length > 0 ? recent_achievements.slice(0, 3).map((a, index) => (
                                    <Fade in timeout={2000 + index * 200} key={a.id}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                background: a.is_unlocked
                                                    ? `${theme.palette.gamification.success}15`
                                                    : `${theme.palette.grey[100]}`,
                                                border: a.is_unlocked
                                                    ? `2px solid ${theme.palette.gamification.success}30`
                                                    : `1px solid ${theme.palette.divider}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar
                                                    sx={{
                                                        width: 35,
                                                        height: 35,
                                                        background: a.is_unlocked
                                                            ? theme.palette.gamification.success
                                                            : theme.palette.grey[400],
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {a.is_unlocked ? '🏆' : '🔒'}
                                                </Avatar>
                                                <Box sx={{flexGrow: 1}}>
                                                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                        {a.achievement.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {a.achievement.description}
                                                    </Typography>
                                                </Box>
                                                {a.is_unlocked && (
                                                    <Chip
                                                        label="Unlocked"
                                                        size="small"
                                                        sx={{
                                                            background: theme.palette.gamification.success,
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Fade>
                                )) : (
                                    <Box sx={{textAlign: 'center', py: 3}}>
                                        <Typography variant="body2" color="text.secondary">
                                            Complete quizzes to unlock achievements! 🎯
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Leaderboard */}
                <Grid size={{xs: 12, md: 4}}>
                    <Fade in timeout={2000}>
                        <Paper elevation={0} sx={{p: 3, height: '100%'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 3}}>
                                <Typography variant="h6" sx={{fontWeight: 700, color: theme.palette.primary.main}}>
                                    <LeaderboardIcon sx={{mr: 1, color: theme.palette.gamification.secondary}}/>
                                    Leaderboard
                                </Typography>
                                <Tooltip title="Your ranking among all students">
                                    <IconButton size="small">
                                        <InfoIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            <Stack spacing={2}>
                                {/* Mini Leaderboard showing players around user */}
                                {leaderboardData ? (
                                    <Stack spacing={1}>
                                        {(() => {
                                            const topUsers = leaderboardData?.entries || [];
                                            const currentUser = leaderboardData?.current_user_entry;
                                            
                                            // Create a combined list with context around current user
                                            let playersToShow = [];
                                            
                                            // If user is in top list, show more context around them
                                            const userInTop = topUsers.some(u => u.user_id === userId);
                                            
                                            if (userInTop) {
                                                // Show users around current position in top list
                                                const userIndex = topUsers.findIndex(u => u.user_id === userId);
                                                const startIndex = Math.max(0, userIndex - 2);
                                                const endIndex = Math.min(topUsers.length - 1, userIndex + 2);
                                                playersToShow = topUsers.slice(startIndex, endIndex + 1);
                                            } else if (currentUser) {
                                                // Show top 2 players, then "..." if gap is large, then current user
                                                const topPlayers = topUsers.slice(0, 2);
                                                const currentRank = currentUser.rank_position || 0;
                                                
                                                playersToShow = [...topPlayers];
                                                
                                                // Add separator if there's a big gap
                                                if (currentRank > 3) {
                                                    playersToShow.push({ 
                                                        isSeparator: true, 
                                                        rank_position: '...' 
                                                    });
                                                }
                                                
                                                playersToShow.push({ ...currentUser, isCurrentUser: true });
                                            } else {
                                                // Fallback to top 5 players
                                                playersToShow = topUsers.slice(0, 5);
                                            }
                                            
                                            return playersToShow.map((user, index) => {
                                                // Handle separator
                                                if (user.isSeparator) {
                                                    return (
                                                        <Box 
                                                            key="separator"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                py: 1
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                ⋮
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                                
                                                return (
                                                    <Box 
                                                        key={user.user_id || index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            p: 1.5,
                                                            borderRadius: 1,
                                                            background: user.user_id === userId || user.isCurrentUser
                                                                ? `${theme.palette.primary.main}15`
                                                                : theme.palette.background.default,
                                                            border: user.user_id === userId || user.isCurrentUser
                                                                ? `2px solid ${theme.palette.primary.main}40`
                                                                : '1px solid transparent',
                                                            transition: 'all 0.2s ease',
                                                            transform: user.user_id === userId || user.isCurrentUser ? 'scale(1.02)' : 'scale(1)',
                                                            '&:hover': {
                                                                background: user.user_id === userId || user.isCurrentUser
                                                                    ? `${theme.palette.primary.main}25`
                                                                    : `${theme.palette.grey[100]}`,
                                                                transform: 'scale(1.02)',
                                                            }
                                                        }}
                                                    >
                                                        {/* Rank */}
                                                        <Box
                                                            sx={{
                                                                minWidth: 35,
                                                                height: 35,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: user.rank_position <= 3
                                                                    ? (user.rank_position === 1 ? theme.palette.gamification.gold :
                                                                       user.rank_position === 2 ? theme.palette.gamification.silver :
                                                                       theme.palette.gamification.bronze)
                                                                    : (user.user_id === userId || user.isCurrentUser 
                                                                        ? theme.palette.primary.main 
                                                                        : theme.palette.grey[300]),
                                                                color: user.rank_position <= 3 || user.user_id === userId || user.isCurrentUser ? 'white' : 'black',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold',
                                                                boxShadow: user.user_id === userId || user.isCurrentUser ? 2 : 0
                                                            }}
                                                        >
                                                            #{user.rank_position || user.rank || '-'}
                                                        </Box>

                                                        {/* User Info */}
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="body2" sx={{ 
                                                                fontWeight: user.user_id === userId || user.isCurrentUser ? 700 : 500,
                                                                color: user.user_id === userId || user.isCurrentUser ? theme.palette.primary.main : 'inherit'
                                                            }}>
                                                                {user.user_id === userId || user.isCurrentUser 
                                                                    ? 'You' 
                                                                    : (user.username || 'Player')}
                                                                {(user.user_id === userId || user.isCurrentUser) && (
                                                                    <Chip
                                                                        label="You"
                                                                        size="small"
                                                                        sx={{
                                                                            ml: 1,
                                                                            height: 18,
                                                                            fontSize: '0.6rem',
                                                                            background: theme.palette.primary.main,
                                                                            color: 'white'
                                                                        }}
                                                                    />
                                                                )}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Level {user.current_level || user.level || 1} • {(user.total_coins || 0).toLocaleString()} coins
                                                            </Typography>
                                                        </Box>

                                                        {/* XP */}
                                                        <Typography variant="body2" sx={{ 
                                                            fontWeight: 600,
                                                            color: user.user_id === userId || user.isCurrentUser ? theme.palette.primary.main : theme.palette.text.primary
                                                        }}>
                                                            {(user.total_xp || 0).toLocaleString()} XP
                                                        </Typography>
                                                    </Box>
                                                );
                                            });
                                        })()}
                                    </Stack>
                                ) : (
                                    <Box sx={{textAlign: 'center', py: 3}}>
                                        <Typography variant="body2" color="text.secondary">
                                            Loading leaderboard...
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{textAlign: 'center', py: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        🏆 Live leaderboard • Compete with fellow students!
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Recent Activity */}
                <Grid size={{xs: 12, md: 4}}>
                    <Fade in timeout={2200}>
                        <Paper elevation={0} sx={{p: 3, height: '100%'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 3}}>
                                <Typography variant="h6" sx={{fontWeight: 700, color: theme.palette.primary.main}}>
                                    <TimelineIcon sx={{mr: 1, color: theme.palette.gamification.primary}}/>
                                    Recent Activity
                                </Typography>
                            </Stack>

                            <Stack spacing={1.5}>
                                {recent_transactions && recent_transactions.length > 0 ? recent_transactions.slice(0, 4).map((tx, index) => (
                                    <Fade in timeout={2400 + index * 100} key={tx.id}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: 1,
                                                background: theme.palette.background.default,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    background: `${theme.palette.primary.main}08`,
                                                    transform: 'translateX(4px)'
                                                }
                                            }}
                                        >
                                            <Chip
                                                label={tx.type}
                                                size="small"
                                                sx={{
                                                    background: tx.type === 'coins_spent'
                                                        ? theme.palette.error.main
                                                        : (tx.amount > 0
                                                            ? theme.palette.gamification.success
                                                            : theme.palette.gamification.secondary),
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    minWidth: '70px',
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                            <Box sx={{flexGrow: 1}}>
                                                <Typography variant="caption" sx={{fontWeight: 500, display: 'block'}}>
                                                    {tx.description || 'Activity completed'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary"
                                                            sx={{fontSize: '0.7rem'}}>
                                                    {new Date(tx.created_at).toLocaleDateString('de-DE', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: tx.type === 'coins_spent'
                                                        ? theme.palette.error.dark
                                                        : (tx.amount > 0 ? theme.palette.success.dark : theme.palette.text.secondary),
                                                    minWidth: '40px',
                                                    textAlign: 'right'
                                                }}
                                            >
                                                {tx.type === 'coins_spent' ? `-${tx.amount}` : (tx.amount > 0 ? `+${tx.amount}` : tx.amount)}
                                            </Typography>
                                        </Box>
                                    </Fade>
                                )) : (
                                    <Box sx={{textAlign: 'center', py: 3}}>
                                        <Typography variant="body2" color="text.secondary">
                                            Your learning activity will appear here 📈
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>
        </Container>
    );
};